import type { ReadStream } from 'node:fs';
import { PostDraftInterface } from '../interfaces/post.interface';
import type {
  AuthorReputationInterface,
  AuthorReputationInterfaceDDB,
} from '../interfaces/reputation.interface';

// export enum UserRoles {
//   MANAGER = 'manager',
//   ADMIN = 'admin',
//   DEV = 'dev',
// }

export interface IUploader {
  userAvatarUploadResolver: (
    // biome-ignore lint/suspicious/noExplicitAny: GraphQL resolver parent/root arg is untyped
    parent: any,
    args: {
      input: UserAvatarUploadInput;
    },
  ) => Promise<UploadedFileResponse>;
}

type FileComponents = {
  filename: string;
  mimetype: string;
  encoding: string;
};

export interface UploadedFileResponse extends FileComponents {
  url: string;
}

export interface FileUpload extends FileComponents {
  stream: ReadStream;
}

export interface UserAvatarUploadInput {
  refreshToken: string;
  userId: string;
  file: FileUpload;
}

export interface UserState {
  userStore: UserSliceState;
}

export type UserSliceState = {
  loggedInUser: UserLoginPayloadDDB;
  user: CoreUserDDB;
  success: boolean;
  loginUserInit: boolean;
  deleteAccount: boolean;
  getUserInit: boolean;
  updateUserInit: boolean;
};

export interface UserLoginPayloadDDB {
  id: string;
  email: string;
  accessToken: string;
  newUser: boolean | undefined;
  firstName: string;
  lastName: string;
  avatar: string;
  bio: string;
  interests: string;
  role: string;
  country: string;
  username: string;
  refreshToken: string;
  success: false;
  isLoggedIn: false;
  userPreferences: UserPreferences;
  lastActive: Date | undefined;
  createdAt: Date | undefined;
  reputation: AuthorReputationInterfaceDDB;
  active: boolean | undefined;
}
// TODO: Remove UserLoginPayload
export interface UserLoginPayload {
  id: string;
  newUser: boolean;
  username: string;
  accessToken: string;
  refreshToken: string;
  success: false;
  isLoggedIn: false;
  userPreferences: UserPreferences;
  lastActive: Date | undefined;
}

export interface UserTable extends CoreUserDDB {
  // Partition key
  PK: string; // USER#userId
  SK: string; // METADATA#userId
  // GSI for username lookups
  GSI1PK: string; // USERNAME#username
  GSI1SK: string; // USER#userId
}

export interface CoreUserDDB {
  username: string;
  bio: string;
  memberSince: string; // ISO date
  active: boolean; // is the membership active
  lastActive: Date | undefined;
  createdAt: string;
  newUser: boolean | undefined;
  userPreferences?: UserPreferencesDDB;
  userProfile?: UserProfile;
  reputation?: AuthorReputationInterfaceDDB;
  drafts?: PostDraftInterface[];
  upVotedPosts?: string[]; // store the IDs of the posts up voted
  downVotedPosts?: string[]; // store the IDs of the posts down voted
  acceptTerms?: boolean;
  accessToken?: string;
  refreshToken?: string;
  // Interests (for recommendation matching)
  interests: string[]; // Array of category identifiers
  id: string;
  avatar: string; // IF use avatar url ELSE display user's initials
  companyName: string;
  country: string;
  zipPostalCode: string;
  email: string;
  firstName: string;
  jobTitle: string;
  lastName: string;
  phoneNumber: string;
  password?: string;
  passwordConfirm?: string;
  role?: 'reader' | 'author' | 'moderator' | undefined;
}

export interface UpdateUserDDB {
  interests: string[] | undefined;
  bio: string;
  companyName: string;
  country: string;
  zipPostalCode: string;
  email: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  phoneNumber: string;
  username: string;
  password?: string;
  passwordConfirm?: string;
  role?: 'reader' | 'author' | 'moderator';
}

// TODO: Remove UpdateUser
export interface UpdateUser {
  interests: string[] | undefined;
  bio: string;
  companyName: string;
  country: string;
  zipPostalCode: string;
  email: string;
  firstName: string;
  jobTitle: string;
  lastName: string;
  phoneNumber: string;
  username: string;
  password?: string;
  passwordConfirm?: string;
  role?: 'reader' | 'author' | 'moderator';
}

// TODO: Remove CoreUser
export interface CoreUser {
  userId: {
    _id: string | undefined;
  };
  interests: string[] | undefined;
  newUser: boolean;
  avatar: string; // IF use avatar url ELSE display user's initials
  bio: string;
  companyName: string;
  country: string;
  zipPostalCode: string;
  email: string;
  firstName: string;
  jobTitle: string;
  lastName: string;
  phoneNumber: string;
  username: string;
  password?: string;
  passwordConfirm?: string;
  role?: 'reader' | 'author' | 'moderator';
}

export type UserSignUp = UserInterface;
export type UserLogin = UserInterface;

// TODO: Remove UserInterface
export interface UserInterface extends CoreUser {
  active: boolean; // is the membership active
  lastActive: Date;
  createdAt: string;
  memberSince: string;
  userPreferences?: UserPreferences;
  reputation?: AuthorReputationInterface;
  follow?: () => void;
  subscribe?: () => void;
  upVotedPosts?: string[]; // store the IDs of the posts up voted
  downVotedPosts?: string[]; // store the IDs of the posts down voted
  acceptTerms?: boolean;
  accessToken?: string;
  refreshToken?: string;
  _doc?: Document | undefined;
}

export enum ThemeEnum {
  // eslint-disable-next-line no-unused-vars
  LIGHT = 'LIGHT',
  // eslint-disable-next-line no-unused-vars
  DARK = 'DARK',
  // eslint-disable-next-line no-unused-vars
  SYSTEM = 'SYSTEM',
}

export enum LandingPageEnum {
  // eslint-disable-next-line no-unused-vars
  FEED = 'FEED',
  // eslint-disable-next-line no-unused-vars
  DASHBOARD = 'DASHBOARD',
}

export interface UserPreferences {
  theme: ThemeEnum | undefined;
  landingPage: LandingPageEnum | undefined;
  avatarUrl?: string | null;
  // TRIL-116: Plan C v1 nudge dismissal + opt-out persistence.
  planCNudgeDismissedAt?: string | null;
  planCNudgeDismissCount?: number;
  planCPromptDismissCount?: number;
  planCOptedOut?: boolean;
  // TRIL-126: collapsed state of the nav rail. Server canonical; localStorage mirror.
  navCollapsed?: boolean;
}

export interface UserPreferencesDDB {
  theme: ThemeEnum | undefined;
  landingPage: LandingPageEnum | undefined;
  avatarUrl?: string | null;
  // TRIL-116: Plan C v1 nudge dismissal + opt-out persistence.
  planCNudgeDismissedAt?: string | null;
  planCNudgeDismissCount?: number;
  planCPromptDismissCount?: number;
  planCOptedOut?: boolean;
  // TRIL-126: collapsed state of the nav rail. Server canonical; localStorage mirror.
  navCollapsed?: boolean;
}

// TRIL-115: DDB-stored types (snake_case enum values, used by mutations + interfaces)
export type EducationLevel =
  | 'high_school'
  | 'bachelors'
  | 'masters'
  | 'phd'
  | 'professional_degree'
  | 'currently_enrolled';

export type CurrentProgram = 'bachelors' | 'masters' | 'phd' | 'professional';

export type YearsBucket = '0-2' | '3-5' | '6-10' | '11-20' | '20+';

export interface Credential {
  id: string;
  title: string;
  issuer: string;
  year: number;
  expiryYear?: number;
  link?: string;
}

// GraphQL-shape (SCREAMING_CASE) — what the client receives over the wire.
export interface ProfessionalContext {
  expertiseAreas: string[];
  educationLevel: string; // SCREAMING_CASE enum
  currentProgram?: string | null;
  fieldOfStudy?: string | null;
  institution?: string | null;
  jobTitle?: string | null;
  yearsExperience?: string | null;
  credentials: Credential[];
  updatedAt: string;
}

// Form-input shape (matches GraphQL Input).
export interface ProfessionalContextInput {
  expertiseAreas: string[];
  educationLevel: string;
  currentProgram?: string;
  fieldOfStudy?: string;
  institution?: string;
  jobTitle?: string;
  yearsExperience?: string;
  credentials: Credential[];
}

// TRIL-115: mirror of server-side UserProfile.
export interface UserProfile {
  isPublicPerson: boolean;
  displayName?: string | null;
  professionalContext?: ProfessionalContext | null;
}
